import { SchemaAwareResultView } from '../custom-agents/SchemaAwareResultView'
import { ActionItemCard, InsightMetaBand } from '../insights'
import { ComponentChip } from '../primitives/ComponentChip'
import { TIER_PILL_CLASSES, severityTier } from '../primitives/severityTier'
import { cn } from '../../lib/utils'
import { PresenterSection } from './PresenterSection'
import { PM_CONSUMED_KEYS, leftoverRecord, mapActionItems, mapPredictions } from './mappers'

interface Props {
  result: Record<string, unknown>
}

export function PmPresenter({ result }: Props) {
  const predictions = mapPredictions(result.predictions)
  const actions = mapActionItems(result.action_items)
  const leftover = leftoverRecord(result, PM_CONSUMED_KEYS)
  const healthy = result.healthy === true && predictions.length === 0

  return (
    <div className="flex flex-col gap-5">
      {healthy ? (
        <p className="m-0 text-sm text-ink-2">No impending failures in this window.</p>
      ) : null}

      {predictions.map((prediction, i) => {
        const tier = severityTier(prediction.chance)
        const pct = Math.round(Math.max(0, Math.min(1, prediction.chance)) * 100)
        return (
          <section key={`${prediction.failureMode}-${i}`} className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <h2 className="m-0 min-w-0 flex-1 text-15 font-semibold leading-snug text-ink">
                {prediction.failureMode}
                <span
                  className={cn(
                    'ml-2 inline-flex items-center rounded-full px-2 py-px align-middle text-10 font-medium uppercase tracking-wide tabular-nums',
                    TIER_PILL_CLASSES[tier]
                  )}
                >
                  {pct}%
                </span>
              </h2>
            </div>
            {prediction.equipment ? (
              <p className="m-0 text-12 text-ink-3">{prediction.equipment}</p>
            ) : null}
            {prediction.reasoning ? (
              <p className="m-0 text-sm leading-relaxed text-ink-2">{prediction.reasoning}</p>
            ) : null}
            {prediction.recommendedAction ? (
              <p className="m-0 text-sm leading-relaxed text-ink-2">
                {prediction.recommendedAction}
              </p>
            ) : null}
            <InsightMetaBand
              timeframeStart={prediction.eventWindow.start}
              timeframeEnd={prediction.eventWindow.end}
              impact={prediction.impact}
            />
            {prediction.components.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {prediction.components.map((component) => (
                  <ComponentChip key={`${component.scope}:${component.id}`} component={component} />
                ))}
              </div>
            ) : null}
          </section>
        )
      })}

      <PresenterSection
        label="Recommended actions"
        meta={actions.length > 0 ? `${actions.length} steps` : undefined}
      >
        {actions.length === 0 ? (
          <p className="m-0 text-sm text-ink-3">No action items emitted.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {actions.map((action, i) => (
              <ActionItemCard
                key={`${action.title}-${i}`}
                index={i + 1}
                title={action.title}
                description={action.description}
                priority={action.priority}
                components={action.components}
              />
            ))}
          </div>
        )}
      </PresenterSection>

      {leftover ? (
        <PresenterSection label="Other fields">
          <SchemaAwareResultView value={leftover} />
        </PresenterSection>
      ) : null}
    </div>
  )
}
